import { Theme } from '@/types/theme'

type ThemeGenerator = (isDarkMode: boolean) => Theme

export const theme: ThemeGenerator = (isDarkMode: boolean): Theme => ({
  // 🧱 Base surfaces — dark mode is a soft off-black at the app level (not pure #000, avoids harsh contrast), dark grey for cards/menus/sections
  primary: isDarkMode ? '#0A0A0C' : '#F7F8FA',
  secondary: isDarkMode ? '#101012' : '#FFFFFF',

  // Nested panel inside a card (Settings sub-sections, CRM Permissions groups, plan rows, etc.) —
  sectionBackground: isDarkMode ? '#141417' : '#fafafa',

  // 🎯 Single brand color
  accent: '#4F46E5',

  // 📝 Text system
  text: isDarkMode ? '#E5E7EB' : '#111827',
  secondrytext: isDarkMode ? '#9CA3AF' : '#6B7280',
  mutedText: isDarkMode ? '#6B7280' : '#9CA3AF',
  Messagetext: isDarkMode ? '#E5E7EB' : '#111827',

  // 🔘 Buttons
  buttonText: '#FFFFFF',
  sendButtonPrimary: '#4F46E5',
  sendButtonAccent: '#6366F1',
  sendButtonBorder: 'transparent',

  // 🧱 Borders
  border: isDarkMode ? '#1C1C1F' : '#E5E7EB',
  borderSecondary: isDarkMode ? '#161618' : '#F1F5F9',
  borderStrong: isDarkMode ? '#2A2A2E' : '#D1D5DB',

  // 💬 Surfaces — cards, menus, sections all share the dark grey card tone
  messageBackground: isDarkMode ? '#101012' : '#fafafa',
  inputBackground: isDarkMode ? '#17171A' : '#fafafa',
  loginBackground: isDarkMode ? '#000000' : '#FFFFFF',
  successBackground: isDarkMode ? '#064E3B' : '#ECFDF5',
  successText: isDarkMode ? '#D1FAE5' : '#065F46',
  errorBackground: isDarkMode ? '#7F1D1D' : '#FEE2E2',
  errorText: isDarkMode ? '#FECACA' : '#991B1B',
  warningBackground: isDarkMode ? '#78350F' : '#FEF3C7',
  warningText: isDarkMode ? '#FDE68A' : '#92400E',
  infoBackground: isDarkMode ? '#0C4A6E' : '#E0F2FE',
  infoText: isDarkMode ? '#BAE6FD' : '#0369A1',
  purpleBackground: isDarkMode ? '#4C1D95' : '#EDE9FE',
  purpleText: isDarkMode ? '#DDD6FE' : '#5B21B6',

  // 🖱 Interaction states
  hoverBackground: isDarkMode ? '#17171A' : '#F3F4F6',
  activeBackground: isDarkMode ? '#1E1E22' : '#E5E7EB',

  // 🎨 Subtle gradient (almost flat)
  gradientFrom: isDarkMode ? '#000000' : '#FFFFFF',
  gradientTo: isDarkMode ? '#050506' : '#F9FAFB',

  // 🧭 Sidebar — fixed dark chrome, does not follow the light/dark toggle (matches design system)
  sidebarBg: '#0B0D12',
  sidebarGradientFrom: '#0B0D12',
  sidebarGradientTo: '#0B0D12',
  sidebarText: '#C9CDD4',
  sidebarActiveText: '#FFFFFF',
  sidebarHover: 'rgba(255,255,255,0.05)',
  sidebarActive: 'rgba(79,70,229,0.16)',
  sidebarBorder: 'rgba(255,255,255,0.06)',
  toggleButtonBg: '#4F46E5',
  toggleButtonText: '#FFFFFF',
  toggleButtonBorder: '#4338CA',

  // 🌫 Elevation
  boxShadow: isDarkMode ? '0 6px 24px rgba(0, 0, 0, 0.4)' : '0 6px 24px rgba(0, 0, 0, 0.08)',
  cardShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.24)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
  cardShadowHover: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.32)' : '0 4px 16px rgba(0, 0, 0, 0.08)',

  // 🎨 Brand gradients — consolidated from ad hoc hex literals previously scattered across chat components
  accentGradient: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
  aiGradient: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
  // Darker/muted in dark mode — the full-brightness accentGradient reads as too vivid against a near-black chat background.
  userMessageBackground: isDarkMode
    ? 'linear-gradient(135deg, #3730A3 0%, #4338CA 100%)'
    : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',

  // 🖱 Scrollbar
  scrollbarTrack: isDarkMode ? '#101012' : '#F1F5F9',
  scrollbarThumb: isDarkMode ? '#2E2E33' : '#CBD5E1',
})

export const getScrollbarStyles = (themeObj: Theme): string => `
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: ${themeObj.scrollbarThumb} ${themeObj.scrollbarTrack};
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: ${themeObj.scrollbarTrack};
  border-radius: 6px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: ${themeObj.scrollbarThumb};
  border-radius: 6px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: ${themeObj.scrollbarThumb};
}
`
